import { useEffect, useId, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  AddressBookIcon,
  BriefcaseIcon,
  CircleNotchIcon,
} from "@phosphor-icons/react";
import { createPlaceFromGoogle } from "common/apis";
import { useI18N, usePostBusinessClaimV2 } from "common/hooks";

import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import { useManualLocation } from "@/state/manualLocation";
import OnboardingBusinessSearch, {
  type BusinessSearchSelection,
} from "../components/OnboardingBusinessSearch";
import OnboardingModal from "../components/OnboardingModal";
import OnboardingPrimaryButton from "../components/OnboardingPrimaryButton";
import OnboardingStepHeader from "../components/OnboardingStepHeader";
import { ONBOARDING_BUSINESS_ROLES } from "../constants";
import { onboardingV2BusinessClaimDraftAtom } from "../state/atoms";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";
import {
  EMPTY_BUSINESS_CLAIM,
  type OnboardingBusinessClaim,
  type OnboardingBusinessMatch,
  type OnboardingBusinessRole,
} from "../types";

/** Deliberately loose — enough to catch a typo, not to adjudicate RFC 5322. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ClaimErrors = Partial<Record<keyof OnboardingBusinessClaim, string>>;

/** The order a failed Submit looks for the first field to send focus to. */
const FOCUS_ORDER = [
  "contactName",
  "email",
  "role",
  "roleOther",
  "businessName",
] as const;

const toCoordinate = (value?: string) => {
  const parsed = value ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Business branch — "Let’s find your business" (`16224:292764`), reached by
 * answering step 1 "Business use".
 *
 * The desktop port of native's `BusinessClaimScreen`: the same fields, the
 * same validation and the same claim POST (`createBusinessClaimV2`), which is
 * awaited — the confirmation only follows a claim the backend accepted, and a
 * rejected one keeps the visitor on the form with what they typed.
 *
 * **The business name is not typed into here.** Activating the field swaps the
 * sheet over to the search (`16224:293725`), as the frames draw it; picking a
 * result runs `place-from-google` in lookup mode straight away, which closes
 * the Google billing session and tells us whether the place is already ours
 * (claimed by `business_id`) or not (described by `suggested_business_data`).
 *
 * **"← Back"** returns to step 1 — the one back control in the flow, because
 * the frame draws it and because without it someone who picked "Business use"
 * by mistake would be stuck in a mandatory form.
 *
 * The fields live in `onboardingV2BusinessClaimDraftAtom` rather than in
 * component state, because the docked sheet remounts on every page change.
 */
const BusinessClaimStep = () => {
  const i18n = useI18N();
  const { answers, dismiss, goToStep, submitBusinessClaim } =
    useOnboardingFlow();
  const { stepNumber, totalSteps } = useOnboardingStepNavigation();
  const { manualLocation } = useManualLocation();
  const latitude = toCoordinate(manualLocation?.latitude);
  const longitude = toCoordinate(manualLocation?.longitude);

  const [draft, setDraft] = useAtom(onboardingV2BusinessClaimDraftAtom);
  const claim = draft ?? answers.businessClaim ?? EMPTY_BUSINESS_CLAIM;

  const [errors, setErrors] = useState<ClaimErrors>({});
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync, status } = usePostBusinessClaimV2();
  const isSubmitting = status === "pending";

  const contactNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLInputElement>(null);
  const roleOtherRef = useRef<HTMLInputElement>(null);
  const businessNameRef = useRef<HTMLButtonElement>(null);
  /** Only the latest pick's lookup may write back — see `selectBusiness`. */
  const lookupIdRef = useRef(0);

  const roleErrorId = useId();
  const businessNameErrorId = useId();

  const title = i18n.get("onboardingBusinessClaimTitle");

  /*
   * Coming back from the search puts focus back on the field that opened it.
   * This runs after the step header's own mount effect, which focuses the
   * heading — a child's effects run before its parent's — so it wins.
   */
  const wasSearchingRef = useRef(false);
  useEffect(() => {
    if (wasSearchingRef.current && !isSearching) {
      businessNameRef.current?.focus();
    }
    wasSearchingRef.current = isSearching;
  }, [isSearching]);

  const patch = (next: Partial<OnboardingBusinessClaim>) => {
    setDraft((current) => ({ ...(current ?? claim), ...next }));
  };

  /** Editing a field clears its error, so the form stops nagging as it's fixed. */
  const update = <K extends keyof OnboardingBusinessClaim>(
    key: K,
    value: OnboardingBusinessClaim[K],
  ) => {
    patch({ [key]: value } as Partial<OnboardingBusinessClaim>);
    setErrors((current) =>
      current[key] === undefined ? current : { ...current, [key]: undefined },
    );
  };

  const selectRole = (role: OnboardingBusinessRole) => {
    // Leaving "Something else" drops its free text with it, so a role typed
    // and abandoned is not submitted alongside "Owner".
    patch({ role, roleOther: role === "others" ? claim.roleOther : "" });
    setErrors((current) => ({
      ...current,
      role: undefined,
      roleOther: undefined,
    }));
  };

  /**
   * A pick from the search. The name lands at once so the form feels
   * immediate; the place behind it follows from the lookup.
   *
   * Picks can overlap — choose one, reopen the search and choose another
   * before the first lookup answers — so each lookup carries an id and only
   * the latest one is allowed to write.
   */
  const selectBusiness = async ({
    name,
    placeId,
    sessionToken,
  }: BusinessSearchSelection) => {
    setIsSearching(false);
    setErrors((current) => ({ ...current, businessName: undefined }));
    setLookupError(null);
    patch({ businessName: name, business: undefined });

    const lookupId = ++lookupIdRef.current;
    if (!placeId) {
      setIsResolving(false);
      return;
    }

    setIsResolving(true);
    const result = await createPlaceFromGoogle(
      placeId,
      null,
      sessionToken,
      true,
    );
    if (lookupId !== lookupIdRef.current) return;
    setIsResolving(false);

    let business: OnboardingBusinessMatch | undefined;
    let resolvedName = name;

    if (result.status === "success") {
      // Already ours: claimed by `business.id` (numeric), never `venue.id`.
      const businessId = result.venue.business?.id;
      resolvedName = result.venue.name || name;
      business = {
        placeId: result.venue.placeId || placeId,
        businessId: businessId != null ? String(businessId) : null,
        address: result.venue.address ?? "",
        latitude: result.venue.latitude,
        longitude: result.venue.longitude,
        isSupported: true,
      };
    } else if (result.status === "supported") {
      resolvedName = result.name || name;
      business = {
        placeId: result.placeId,
        businessId: null,
        address: result.address,
        latitude: result.lat ?? undefined,
        longitude: result.lng ?? undefined,
        isSupported: true,
      };
    } else if (result.status === "unsupported") {
      // Accepted, not refused: the confirmation says the business is not
      // visible yet rather than the form turning the visitor away.
      business = {
        placeId,
        businessId: null,
        address: "",
        latitude: result.lat ?? undefined,
        longitude: result.lng ?? undefined,
        isSupported: false,
      };
    } else {
      setLookupError(i18n.get("onboardingBusinessErrorLookup"));
      return;
    }

    patch({ businessName: resolvedName, business });
  };

  const validate = (): ClaimErrors => {
    const next: ClaimErrors = {};

    if (!claim.contactName.trim()) {
      next.contactName = i18n.get("onboardingBusinessErrorName");
    }

    if (!claim.email.trim()) {
      next.email = i18n.get("onboardingBusinessErrorEmail");
    } else if (!EMAIL_PATTERN.test(claim.email.trim())) {
      next.email = i18n.get("onboardingBusinessErrorEmailInvalid");
    }

    if (
      !claim.role ||
      !ONBOARDING_BUSINESS_ROLES.some((role) => role.value === claim.role)
    ) {
      next.role = i18n.get("onboardingBusinessErrorRole");
    } else if (claim.role === "others" && !claim.roleOther.trim()) {
      next.roleOther = i18n.get("onboardingBusinessErrorRoleOther");
    }

    if (!claim.businessName.trim()) {
      next.businessName = i18n.get("onboardingBusinessErrorBusinessName");
    }

    return next;
  };

  /**
   * Each field announces its own error (`role="alert"`), so a failed Submit
   * only has to send focus to the first one the visitor can act on.
   */
  const focusFirstError = (found: ClaimErrors) => {
    const refs = {
      contactName: contactNameRef,
      email: emailRef,
      role: roleRef,
      roleOther: roleOtherRef,
      businessName: businessNameRef,
    };
    const first = FOCUS_ORDER.find((key) => found[key]);
    if (first) refs[first].current?.focus();
  };

  const submit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found);
      return;
    }

    if (lookupError) {
      businessNameRef.current?.focus();
      return;
    }

    setSubmitError(null);

    const trimmed: OnboardingBusinessClaim = {
      ...claim,
      contactName: claim.contactName.trim(),
      email: claim.email.trim(),
      phone: claim.phone.trim(),
      roleOther: claim.roleOther.trim(),
      businessName: claim.businessName.trim(),
      description: claim.description.trim(),
    };

    try {
      await mutateAsync({
        name: trimmed.contactName,
        email: trimmed.email,
        phone: trimmed.phone,
        description: trimmed.description,
        // Validation guarantees a role by this point.
        businessRole: trimmed.role as OnboardingBusinessRole,
        businessRoleOther:
          trimmed.role === "others" ? trimmed.roleOther : undefined,
        // Alternatives: a place we already hold is claimed by id, anything
        // else is described.
        businessId: trimmed.business?.businessId ?? undefined,
        suggestedBusiness: trimmed.business?.businessId
          ? undefined
          : {
              placeId: trimmed.business?.placeId ?? null,
              name: trimmed.businessName,
              formattedAddress: trimmed.business?.address ?? "",
            },
      });
    } catch (error) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : i18n.get("onboardingBusinessErrorSubmit"),
      );
      return;
    }

    submitBusinessClaim(trimmed);
  };

  const isBusy = isResolving || isSubmitting;
  const businessNameError = errors.businessName ?? lookupError ?? undefined;

  if (isSearching) {
    return (
      <OnboardingModal onClose={dismiss} ariaLabel={title}>
        <OnboardingBusinessSearch
          initialTerm={claim.businessName}
          latitude={latitude}
          longitude={longitude}
          onSelect={selectBusiness}
          onClose={() => setIsSearching(false)}
        />
      </OnboardingModal>
    );
  }

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <div className="flex flex-col gap-2">
          {submitError ? (
            <p
              role="alert"
              className="body-s-regular whitespace-pre-line text-fg-error-subtle"
            >
              {submitError}
            </p>
          ) : null}
          <OnboardingPrimaryButton
            label={i18n.get("onboardingBusinessSubmit")}
            // Mid-lookup would send the name without the place behind it;
            // mid-post would file the claim twice.
            disabled={isBusy}
            onClick={submit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <OnboardingStepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          subtitle={i18n.get("onboardingBusinessClaimSubtitle")}
          onBack={() => goToStep("USER_TYPE")}
          backLabel={i18n.get("onboardingBusinessBackLabel")}
        />

        <SectionTitle
          icon={AddressBookIcon}
          title={i18n.get("onboardingBusinessContactSection")}
        />

        <FloatingLabelInput
          ref={contactNameRef}
          type="text"
          label={i18n.get("onboardingBusinessContactName")}
          required
          value={claim.contactName}
          onChange={(event) => update("contactName", event.target.value)}
          errorMessage={errors.contactName}
          maxLength={255}
        />

        <FloatingLabelInput
          ref={emailRef}
          type="email"
          label={i18n.get("onboardingBusinessEmail")}
          required
          value={claim.email}
          onChange={(event) => update("email", event.target.value)}
          errorMessage={errors.email}
          maxLength={255}
        />

        <FloatingLabelInput
          type="tel"
          label={i18n.get("onboardingBusinessPhone")}
          value={claim.phone}
          onChange={(event) => update("phone", event.target.value)}
          maxLength={50}
        />

        {/* "Your role in the business" (`16224:293609`): single-choice chips,
            built on real radios for arrow keys and one tab stop. */}
        <fieldset
          className="m-0 flex flex-col gap-4 border-0 p-0"
          aria-describedby={errors.role ? roleErrorId : undefined}
        >
          <legend className="title-s-semibold mb-4 text-fg-neutral-strong">
            {i18n.get("onboardingBusinessRoleLabel")}
            <span aria-hidden="true" className="text-fg-error-subtle">
              *
            </span>
          </legend>

          <div className="flex flex-wrap gap-2">
            {ONBOARDING_BUSINESS_ROLES.map(({ value, labelKey }, index) => {
              const selected = claim.role === value;
              return (
                <label
                  key={value}
                  className={cn(
                    "flex min-h-10 cursor-pointer items-center justify-center rounded-full px-3 py-2.5 transition-colors",
                    "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focusRing-default",
                    // 2px when selected, as drawn; the 1px of padding it
                    // costs is taken back so the label does not move.
                    selected
                      ? "border-2 border-border-token-brand-subtle bg-bg-brand-tertiary-default px-[11px] py-[9px]"
                      : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover",
                  )}
                >
                  <input
                    ref={index === 0 ? roleRef : undefined}
                    type="radio"
                    name="onboarding-business-role"
                    value={value}
                    checked={selected}
                    onChange={() => selectRole(value)}
                    required
                    className="sr-only"
                  />
                  <span
                    className={cn(
                      selected
                        ? "label-m-semibold text-fg-brand-subtle"
                        : "label-m-medium text-fg-neutral-strong",
                    )}
                  >
                    {i18n.get(labelKey)}
                  </span>
                </label>
              );
            })}
          </div>

          {errors.role ? (
            <p
              id={roleErrorId}
              role="alert"
              className="body-s-regular text-fg-error-subtle"
            >
              {errors.role}
            </p>
          ) : null}
        </fieldset>

        {claim.role === "others" ? (
          <FloatingLabelInput
            ref={roleOtherRef}
            type="text"
            label={i18n.get("onboardingBusinessRoleOther")}
            required
            value={claim.roleOther}
            onChange={(event) => update("roleOther", event.target.value)}
            errorMessage={errors.roleOther}
            maxLength={255}
          />
        ) : null}

        <div className="h-px w-full bg-border-token-neutral-minimal" />

        <SectionTitle
          icon={BriefcaseIcon}
          title={i18n.get("onboardingBusinessInfoSection")}
        />

        {/* Business name: drawn as a text field, but it opens the search
            rather than taking typing. A button, so that is what it announces. */}
        <div className="flex flex-col gap-2">
          <button
            ref={businessNameRef}
            type="button"
            onClick={() => setIsSearching(true)}
            aria-describedby={
              businessNameError ? businessNameErrorId : undefined
            }
            className={cn(
              "focus-blue flex min-h-12 w-full flex-col items-start justify-center rounded-lg px-3 py-1.5 text-left",
              businessNameError
                ? "border border-border-token-error-subtle bg-bg-error-tertiary-default"
                : claim.businessName
                  ? "border-2 border-border-token-neutral-minimal bg-container-brand-minimal"
                  : "border border-border-token-neutral-subtle",
            )}
          >
            <span
              className={cn(
                claim.businessName
                  ? "label-s-regular text-fg-brand-subtle"
                  : "label-m-medium text-fg-neutral-subtle",
                businessNameError && "text-fg-error-strong",
              )}
            >
              {i18n.get("onboardingBusinessName")}{" "}
              <span className="text-fg-error-subtle">*</span>
            </span>
            {claim.businessName ? (
              <span className="label-m-medium w-full truncate text-fg-neutral-strong">
                {claim.businessName}
              </span>
            ) : null}
            <span className="sr-only">
              {i18n.get("onboardingBusinessNameHint")}
            </span>
          </button>

          {businessNameError ? (
            <p
              id={businessNameErrorId}
              role="alert"
              className="body-s-regular text-fg-error-subtle"
            >
              {businessNameError}
            </p>
          ) : null}

          {isResolving ? (
            <p
              role="status"
              className="body-s-regular flex items-center gap-2 text-fg-neutral-subtle"
            >
              <CircleNotchIcon
                size={16}
                aria-hidden="true"
                className="animate-spin text-fg-brand-subtle"
              />
              {i18n.get("onboardingBusinessChecking")}
            </p>
          ) : null}
        </div>

        <textarea
          value={claim.description}
          onChange={(event) => update("description", event.target.value)}
          placeholder={i18n.get("onboardingBusinessDescription")}
          aria-label={i18n.get("onboardingBusinessDescription")}
          maxLength={2000}
          className="focus-blue label-m-medium h-[120px] w-full resize-none rounded-lg border border-border-token-neutral-subtle bg-bg-neutral-secondary-default p-4 text-fg-neutral-strong placeholder:text-fg-neutral-subtle"
        />
      </div>
    </OnboardingModal>
  );
};

/** Icon + section heading (`1870:26612`). */
const SectionTitle = ({
  icon: IconComponent,
  title,
}: {
  icon: typeof AddressBookIcon;
  title: string;
}) => (
  <h3 className="title-s-semibold flex items-center gap-2 text-fg-neutral-strong">
    <IconComponent
      size={24}
      weight="fill"
      aria-hidden="true"
      className="shrink-0 text-fg-brand-subtle"
    />
    {title}
  </h3>
);

export default BusinessClaimStep;
